(function () {
  const OPEN_CLASS = "is-open";
  const POPULAR_BRANDS = new Set(["kershaw", "civivi", "spyderco", "zero tolerance", "vosteed", "olight"]);
  const FALLBACK_MIN_BRANDS = 12;

  function filterMenu(menu, query) {
    const normalized = query.trim().toLowerCase();
    const groups = Array.from(menu.querySelectorAll("[data-brand-group]"));
    let visibleCount = 0;

    groups.forEach((group) => {
      let groupCount = 0;
      group.querySelectorAll("[data-brand-item]").forEach((item) => {
        const name = item.dataset.brandName || "";
        const matches = !normalized || name.includes(normalized);
        item.hidden = !matches;
        if (matches) {
          groupCount += 1;
          visibleCount += 1;
        }
      });
      group.hidden = groupCount === 0;
    });

    const empty = menu.querySelector("[data-brand-empty]");
    if (empty) empty.hidden = visibleCount !== 0;

    menu.querySelectorAll("[data-brand-letter-jump]").forEach((button) => {
      const letter = button.dataset.brandLetterJump;
      const group = groups.find((candidate) => candidate.dataset.brandLetter === letter);
      button.hidden = Boolean(normalized) && (!group || group.hidden);
    });
  }

  function initSearch(menu) {
    if (menu.dataset.brandSearchReady === "true") return;
    menu.dataset.brandSearchReady = "true";

    const search = menu.querySelector("[data-brand-search]");
    if (!search) return;

    search.addEventListener("input", () => filterMenu(menu, search.value));
    search.addEventListener("search", () => filterMenu(menu, search.value));

    menu.querySelectorAll("[data-brand-letter-jump]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        const letter = button.dataset.brandLetterJump;
        const target = menu.querySelector(`[data-brand-group][data-brand-letter="${CSS.escape(letter)}"]`);
        if (!target || target.hidden) return;

        menu.querySelectorAll("[data-brand-letter-jump]").forEach((item) => item.classList.remove("is-active"));
        button.classList.add("is-active");
        target.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start",
        });
      });
    });
  }

  function initDesktop(nav) {
    if (nav.dataset.brandNavReady === "true") return;
    nav.dataset.brandNavReady = "true";

    const trigger = nav.querySelector("[data-brand-trigger]");
    const menu = nav.querySelector('[data-bftp-brand-menu][data-brand-mode="desktop"]');
    if (!trigger || !menu) return;

    initSearch(menu);
    let closeTimer;

    const open = (focusSearch) => {
      window.clearTimeout(closeTimer);
      nav.classList.add(OPEN_CLASS);
      trigger.setAttribute("aria-expanded", "true");
      menu.setAttribute("aria-hidden", "false");
      if (focusSearch) {
        window.setTimeout(() => menu.querySelector("[data-brand-search]")?.focus(), 40);
      }
    };

    const close = (restoreFocus) => {
      nav.classList.remove(OPEN_CLASS);
      trigger.setAttribute("aria-expanded", "false");
      menu.setAttribute("aria-hidden", "true");
      const search = menu.querySelector("[data-brand-search]");
      if (search && search.value) {
        search.value = "";
        filterMenu(menu, "");
      }
      if (restoreFocus) trigger.focus();
    };

    const delayedClose = () => {
      closeTimer = window.setTimeout(() => {
        if (!nav.matches(":focus-within") && !nav.matches(":hover")) close(false);
      }, 140);
    };

    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      const supportsHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      if (supportsHover) {
        open(false);
      } else if (nav.classList.contains(OPEN_CLASS)) {
        close(false);
      } else {
        open(false);
      }
    });

    nav.addEventListener("mouseenter", () => open(false));
    nav.addEventListener("mouseleave", delayedClose);
    nav.addEventListener("focusout", delayedClose);

    document.addEventListener("click", (event) => {
      if (nav.classList.contains(OPEN_CLASS) && !nav.contains(event.target)) close(false);
    });

    document.addEventListener("keydown", (event) => {
      if (!nav.classList.contains(OPEN_CLASS)) return;
      if (event.key === "Escape") {
        event.preventDefault();
        close(true);
      }
      if (event.key === "/" && !event.metaKey && !event.ctrlKey && !event.altKey) {
        const tag = document.activeElement?.tagName;
        if (tag !== "INPUT" && tag !== "TEXTAREA") {
          event.preventDefault();
          open(true);
        }
      }
    });
  }

  function initMobile(menu) {
    if (menu.dataset.brandMobileReady === "true") return;
    menu.dataset.brandMobileReady = "true";
    initSearch(menu);

    const details = menu.closest("details");
    if (!details) return;

    details.addEventListener("toggle", () => {
      if (!details.open) {
        const search = menu.querySelector("[data-brand-search]");
        if (search && search.value) {
          search.value = "";
          filterMenu(menu, "");
        }
      }
    });
  }

  function sameStoreRoot(href) {
    if (!href) return false;
    try {
      const url = new URL(href, window.location.origin);
      return url.origin === window.location.origin && url.pathname === "/" && !url.search && !url.hash;
    } catch (error) {
      return false;
    }
  }

  function normalizedHref(href) {
    try {
      return new URL(href, window.location.origin).href;
    } catch (error) {
      return href || "";
    }
  }

  function readDirectLinks(list) {
    if (!list) return [];
    return Array.from(list.children)
      .map((item) => item.querySelector(":scope > a"))
      .filter(Boolean)
      .map((anchor) => ({
        title: anchor.textContent.trim(),
        href: anchor.getAttribute("href") || "#",
        current: anchor.getAttribute("aria-current") === "page",
      }))
      .filter((item) => item.title);
  }

  function createSvg(kind) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");

    if (kind === "search") {
      const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      circle.setAttribute("cx", "11");
      circle.setAttribute("cy", "11");
      circle.setAttribute("r", "6.5");
      circle.setAttribute("stroke", "currentColor");
      circle.setAttribute("stroke-width", "1.8");
      svg.append(circle);

      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", "m16 16 4 4");
      path.setAttribute("stroke", "currentColor");
      path.setAttribute("stroke-width", "1.8");
      path.setAttribute("stroke-linecap", "round");
      svg.append(path);
    } else {
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", "M5 12h13M13 7l5 5-5 5");
      path.setAttribute("stroke", "currentColor");
      path.setAttribute("stroke-width", "1.6");
      path.setAttribute("stroke-linecap", "round");
      path.setAttribute("stroke-linejoin", "round");
      svg.append(path);
    }

    return svg;
  }

  function createCaret() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("class", "icon icon-caret");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("viewBox", "0 0 10 6");
    svg.setAttribute("fill", "none");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M1 1l4 4 4-4");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "1.4");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    svg.append(path);
    return svg;
  }

  function makeTextElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    element.textContent = text;
    return element;
  }

  function buildSearch(mode) {
    const label = document.createElement("label");
    label.className = `bftp-brand-menu__search${mode === "mobile" ? " bftp-brand-menu__search--mobile" : ""}`;
    label.append(makeTextElement("span", "visually-hidden", "Search brands"));
    label.append(createSvg("search"));

    const input = document.createElement("input");
    input.type = "search";
    input.placeholder = "Search brands...";
    input.autocomplete = "off";
    input.dataset.brandSearch = "";
    label.append(input);

    if (mode === "desktop") label.append(makeTextElement("kbd", "", "/"));
    return label;
  }

  function buildBrandMenu(links, mode, menuId) {
    const menu = document.createElement("div");
    menu.id = menuId;
    menu.className = `bftp-brand-menu bftp-brand-menu--${mode}`;
    menu.dataset.bftpBrandMenu = "";
    menu.dataset.brandMode = mode;
    if (mode === "desktop") menu.setAttribute("aria-hidden", "true");

    const inner = document.createElement("div");
    inner.className = `bftp-brand-menu__inner${mode === "desktop" ? " container" : ""}`;
    menu.append(inner);

    if (mode === "desktop") {
      const headingRow = document.createElement("div");
      headingRow.className = "bftp-brand-menu__heading-row";
      const heading = document.createElement("div");
      heading.append(makeTextElement("p", "bftp-brand-menu__eyebrow", "Find your maker"));
      heading.append(makeTextElement("h2", "bftp-brand-menu__title", "Shop By Brand"));
      headingRow.append(heading, buildSearch(mode));
      inner.append(headingRow);

      const popular = document.createElement("div");
      popular.className = "bftp-brand-menu__popular";
      popular.dataset.brandPopular = "";
      popular.append(makeTextElement("span", "bftp-brand-menu__popular-label", "Popular"));
      links.forEach((item) => {
        if (!POPULAR_BRANDS.has(item.title.toLowerCase())) return;
        const anchor = document.createElement("a");
        anchor.href = item.href;
        anchor.className = "bftp-brand-menu__popular-link";
        anchor.textContent = item.title;
        popular.append(anchor);
      });
      inner.append(popular);
    } else {
      const mobileHeader = document.createElement("div");
      mobileHeader.className = "bftp-brand-menu__mobile-header";
      mobileHeader.append(makeTextElement("p", "bftp-brand-menu__eyebrow", "Browse all makers"));
      mobileHeader.append(makeTextElement("h2", "bftp-brand-menu__title", "Shop By Brand"));
      inner.append(mobileHeader, buildSearch(mode));
    }

    const groups = new Map();
    links.forEach((item) => {
      const letter = item.title.slice(0, 1).toUpperCase();
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter).push(item);
    });

    const letters = document.createElement("div");
    letters.className = "bftp-brand-menu__letters";
    letters.setAttribute("aria-label", "Jump to brand letter");
    letters.dataset.brandLetters = "";

    groups.forEach((items, letter) => {
      const anchor = document.createElement("a");
      anchor.href = `#${menuId}-${letter.toLowerCase()}`;
      anchor.className = "bftp-brand-menu__letter";
      anchor.dataset.brandLetterJump = letter;
      anchor.setAttribute("aria-label", `Jump to ${letter} brands`);
      anchor.textContent = letter;
      letters.append(anchor);
    });
    inner.append(letters);

    const groupsWrap = document.createElement("div");
    groupsWrap.className = "bftp-brand-menu__groups";
    groupsWrap.dataset.brandGroups = "";

    groups.forEach((items, letter) => {
      const section = document.createElement("section");
      section.id = `${menuId}-${letter.toLowerCase()}`;
      section.className = "bftp-brand-menu__group";
      section.dataset.brandGroup = "";
      section.dataset.brandLetter = letter;
      section.append(makeTextElement("h3", "bftp-brand-menu__group-letter", letter));

      const list = document.createElement("ul");
      list.className = "bftp-brand-menu__list";
      list.setAttribute("role", "list");

      items.forEach((item) => {
        const li = document.createElement("li");
        li.dataset.brandItem = "";
        li.dataset.brandName = item.title.toLowerCase();

        const anchor = document.createElement("a");
        anchor.href = item.href;
        anchor.className = `bftp-brand-menu__link${item.current ? " is-current" : ""}`;
        if (item.current) anchor.setAttribute("aria-current", "page");
        anchor.append(makeTextElement("span", "", item.title), createSvg("arrow"));
        li.append(anchor);
        list.append(li);
      });

      section.append(list);
      groupsWrap.append(section);
    });
    inner.append(groupsWrap);

    const empty = makeTextElement("p", "bftp-brand-menu__empty", "No brands match that search.");
    empty.dataset.brandEmpty = "";
    empty.hidden = true;
    inner.append(empty);

    return menu;
  }

  function ensureFallbackStyles() {
    if (document.querySelector("style[data-bftp-brand-fallback-style]")) return;
    const style = document.createElement("style");
    style.dataset.bftpBrandFallbackStyle = "";
    style.textContent = "@media screen and (max-width:1099px){.bftp-brand-menu--mobile .bftp-brand-menu__letters{position:static;display:grid;grid-template-columns:repeat(auto-fit,minmax(4.4rem,1fr));gap:.5rem;margin:0;padding:1rem 0 1.2rem;overflow:visible;background:rgb(var(--color-background));border-bottom:1px solid rgb(var(--color-border))}.bftp-brand-menu--mobile .bftp-brand-menu__letter{width:100%;min-width:0;height:4.4rem;border-radius:.8rem}}";
    document.head.append(style);
  }

  function upgradeDesktopBrandMenu(root) {
    const scope = root || document;
    if (scope.querySelector("[data-bftp-brand-nav]")) {
      const existing = scope.querySelector('[data-bftp-brand-nav] [data-bftp-brand-menu][data-brand-mode="desktop"]');
      if (existing) {
        return Array.from(existing.querySelectorAll("[data-brand-item] a")).map((anchor) => normalizedHref(anchor.href));
      }
    }

    const candidates = scope.querySelectorAll(".header__inline-menu > .list-menu > li");
    for (const nav of candidates) {
      const trigger = nav.querySelector(":scope > a.header__menu-item");
      const submenu = nav.querySelector(":scope > ul.header__submenu");
      if (!trigger || !submenu || !sameStoreRoot(trigger.getAttribute("href"))) continue;

      const links = readDirectLinks(submenu);
      if (links.length < FALLBACK_MIN_BRANDS) continue;

      const menuId = "BftpBrandMenu-dynamic-desktop";
      const button = document.createElement("button");
      button.type = "button";
      button.className = `${trigger.className} bftp-brand-menu-trigger`;
      button.setAttribute("aria-expanded", "false");
      button.setAttribute("aria-controls", menuId);
      button.dataset.brandTrigger = "";

      const label = trigger.querySelector("span")?.cloneNode(true) || makeTextElement("span", "", trigger.textContent.trim());
      label.append(createCaret());
      button.append(label);

      trigger.replaceWith(button);
      submenu.remove();
      nav.classList.add("bftp-brand-nav");
      nav.dataset.bftpBrandNav = "";
      nav.append(buildBrandMenu(links, "desktop", menuId));
      ensureFallbackStyles();
      return links.map((item) => normalizedHref(item.href));
    }

    return [];
  }

  function upgradeMobileBrandMenu(root, desktopBrandHrefs) {
    const scope = root || document;
    if (scope.querySelector('[data-bftp-brand-menu][data-brand-mode="mobile"]')) return;
    if (!desktopBrandHrefs.length) return;

    const brandSet = new Set(desktopBrandHrefs);
    const detailsList = scope.querySelectorAll("header-drawer .menu-drawer__navigation > ul.menu-drawer__menu > li > details");

    for (const details of detailsList) {
      const panel = details.querySelector(":scope > .menu-drawer__submenu");
      const inner = panel?.querySelector(":scope > .menu-drawer__inner-submenu");
      const list = inner?.querySelector(":scope > ul.menu-drawer__menu");
      if (!panel || !inner || !list) continue;

      const links = readDirectLinks(list);
      if (links.length < FALLBACK_MIN_BRANDS) continue;
      const matches = links.filter((item) => brandSet.has(normalizedHref(item.href))).length;
      if (matches < Math.min(FALLBACK_MIN_BRANDS, Math.ceil(links.length * 0.6))) continue;

      panel.classList.add("bftp-brand-drawer-panel");
      inner.classList.add("bftp-brand-drawer");
      list.remove();
      inner.append(buildBrandMenu(links, "mobile", "BftpBrandMenu-dynamic-mobile"));
      ensureFallbackStyles();
      return;
    }
  }

  function upgradeFallbackBrandMenus(root) {
    const desktopBrandHrefs = upgradeDesktopBrandMenu(root);
    upgradeMobileBrandMenu(root, desktopBrandHrefs);
  }

  function initBrandMenus(root) {
    const scope = root || document;
    upgradeFallbackBrandMenus(scope);
    scope.querySelectorAll("[data-bftp-brand-nav]").forEach(initDesktop);
    scope.querySelectorAll('[data-bftp-brand-menu][data-brand-mode="mobile"]').forEach(initMobile);
  }

  document.addEventListener("DOMContentLoaded", () => initBrandMenus(document));
  document.addEventListener("shopify:section:load", (event) => initBrandMenus(event.target));
})();
